import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { Formik } from 'formik'
import * as Yup from 'yup'

import { BenefitProductClassResponse, deleteIchraProductClass, getBenefitProductClasses } from 'core'
import ichraClassSelection from 'core/application/adminDashboard/benefitDetails/design/ichra/classSelectionCommand'

import { Button, Card, Col, DeleteConfirmModal, Icon, Row, Skeleton } from 'common/components'
import { useCommandRequestV2, useQueryRequestV2 } from 'common/hooks'

import { BaseComponentProps } from '../../common/models'
import { FormProps, SaveProductClassForm } from './saveProductClassForm'

type IchraEmployeeClassesProps = {}

const IchraEmployeeClasses = (props: BaseComponentProps<IchraEmployeeClassesProps>) => {
  const { bindSubmitFormHandler } = props
  const { t: translate } = useTranslation()
  const { planPeriod } = useParams() as any

  const [deleteProductClass, setDeleteProductClass] = useState<BenefitProductClassResponse | null>(null)
  const [initialValues] = useState<IchraEmployeeClassesProps>({})
  const [productClassFormValues, setProductClassFormValues] = useState<FormProps | null>(null)

  const { data: productClasses, reload: reloadProductClasses } = useQueryRequestV2<BenefitProductClassResponse[]>({
    queryFn: () => getBenefitProductClasses(planPeriod),
  })

  const { sendCommand: sendDeleteProductClass, isLoading: isDeleteProductClassLoading } = useCommandRequestV2(
    (productClassId: string) => deleteIchraProductClass(productClassId),
  )

  const { sendCommand: sendClassSelectionCommand } = useCommandRequestV2((planPeriodId: string) =>
    ichraClassSelection(planPeriodId),
  )

  useEffect(() => {
    props.setIsFormValid(productClasses?.length > 0)
  }, [productClasses])

  const onEditProductClass = (item: BenefitProductClassResponse) => {
    setProductClassFormValues({
      id: item.id,
      classTypeIds: item.classTypeIds,
      planPeriodId: planPeriod,
      name: item.name,
      stateId: item.stateId,
    })
  }

  const onDeleteClassRequested = (item: BenefitProductClassResponse) => {
    setDeleteProductClass(item)
  }

  const onDeleteProductClass = async () => {
    const result = await sendDeleteProductClass(deleteProductClass?.id)
    if (result.completed) {
      setDeleteProductClass(null)
      await reloadProductClasses()
    }
  }

  const onProductClassSaved = async (productClassId: string) => {
    setProductClassFormValues(null)
    await reloadProductClasses()
  }

  const onSubmit = async (values: IchraEmployeeClassesProps) => {
    const result = await sendClassSelectionCommand(planPeriod)

    if (result.completed) {
      props.goNext()
    } else {
      props.setIsSubmiting(false)
    }
  }

  return (
    <>
      <DeleteConfirmModal
        title={`Deleting '${deleteProductClass?.name}' ...`}
        content={`Are you sure you want to delete ${deleteProductClass?.name}? This action cannot be undone.`}
        onDelete={onDeleteProductClass}
        onCancel={() => {
          setDeleteProductClass(null)
        }}
        deleteButtonLoading={isDeleteProductClassLoading}
        visible={!!deleteProductClass}
      />

      <Formik
        enableReinitialize={true}
        validationSchema={formValidator(translate)}
        initialValues={initialValues}
        onSubmit={onSubmit}
        validate={(values) => {
          props.hasDirtyData(true)
          props.setIsFormValid(formValidator(translate).isValidSync(values))
          return {}
        }}
      >
        {(formik) => {
          const { handleSubmit, submitForm } = formik
          bindSubmitFormHandler(submitForm)
          return (
            <form
              onSubmit={handleSubmit}
              autoComplete="off"
              className={'ichra-employee-classes t-ichra-employee-classes'}
            >
              <h5 className={'text-bold mb-8'}>Create classes</h5>

              <h6 className={'mb-32 color-gray-7'}>
                Classes give you a way to categorize employees into groups that make it easier to customize your
                benefits.
              </h6>

              <Skeleton loading={!productClasses}>
                <h5 className={'text-bold mb-0'}>Your classes</h5>

                {productClasses && (
                  <Row>
                    <Col sm={24} lg={12}>
                      {productClasses.map((item) => (
                        <Card className={'mb-16 p-8 mt-16'}>
                          <Row align={'middle'} justify={'space-between'}>
                            <Col>
                              <h5 className={'mb-0'}>{item.name}</h5>
                            </Col>

                            <Col style={{ width: '80px' }}>
                              <Row align={'middle'} justify={'space-between'}>
                                {item.totalEmployees === 0 && (
                                  <Col>
                                    <Button type={'default'} onClick={() => onEditProductClass(item)}>
                                      Edit
                                    </Button>
                                  </Col>
                                )}

                                {item.totalEmployees === 0 && (
                                  <Col>
                                    <Icon
                                      onClick={() => onDeleteClassRequested(item)}
                                      className={'fal fa-trash-alt delete-class-btn'}
                                    />
                                  </Col>
                                )}
                              </Row>
                            </Col>
                          </Row>
                        </Card>
                      ))}
                    </Col>
                  </Row>
                )}

                {!productClassFormValues && (
                  <>
                    {productClasses?.length === 0 && (
                      <h6 className={'color-gray-7 mb-24'}>Click "Add a class" below to get started.</h6>
                    )}
                    <Button
                      type={'default'}
                      onClick={async () => {
                        setProductClassFormValues({
                          planPeriodId: planPeriod,
                          classTypeIds: [],
                        })
                        await reloadProductClasses()
                      }}
                    >
                      <Icon type="fa fa-plus" className={'mr-4'} fontAwesome /> Add Class
                    </Button>
                  </>
                )}

                {productClassFormValues && (
                  <Row className={'mt-16'}>
                    <Col sm={24} lg={17}>
                      <SaveProductClassForm
                        initialValues={productClassFormValues}
                        productClasses={productClasses}
                        onProductClassSaved={onProductClassSaved}
                        onProductClassCancel={() => {
                          setProductClassFormValues(null)
                        }}
                      />
                    </Col>
                  </Row>
                )}
              </Skeleton>
            </form>
          )
        }}
      </Formik>
    </>
  )
}

const formValidator = (translate: any) => Yup.object({})

export default IchraEmployeeClasses
